// ────────────────────────────────────────────────────────────────────────────
// PROPAGATION D'UN CHANGEMENT DE PRIX CATALOGUE VERS LES DOCUMENTS PROSPACE
//
// Deux règles distinctes, décidées par Khalil (2026-08-01) :
//
// 1. DEVIS — un devis est une PROPOSITION, pas un engagement : quand le prix
//    catalogue d'un de ses articles bouge (à la hausse comme à la baisse), le
//    devis est réaligné sur le nouveau prix et l'électricien est prévenu.
//    C'est aussi ce qui garde « Passer une commande » fonctionnel : le trigger
//    `pro_line_pricing_guard` re-dérive le prix depuis le catalogue à l'INSERT
//    des lignes de commande, donc un devis figé sur un ancien prix ne peut plus
//    être transformé (line_price_mismatch).
//
// 2. COMMANDE — le PRIX de vente est gelé (l'électricien a commandé à ce
//    prix-là), mais notre COÛT d'achat, lui, a bougé : notre bénéfice change,
//    et la commission de l'électricien — un % de ce bénéfice — doit suivre.
//    Le recalcul s'arrête à l'EXPÉDITION : une fois la marchandise partie, la
//    vente est faite, plus rien ne bouge.
//
// Tout est pur ici : les accès DB (quels documents, quel catalogue) vivent dans
// l'adapter, qui n'écrit que les valeurs calculées ci-dessous.
// ────────────────────────────────────────────────────────────────────────────

import {
  calculateProCommission,
  calculateSalesLineBenefit,
  type SalesBenefitLineInput,
} from '@erp/domain/sales';
import { calculateProLineTotals, grossUnitHt, resolveTierLinePricing, round3 } from './pricing';
import { calculateDevisTotals, type ProDevisLineLike } from './product-requests';
import type { CommandeStatus, DevisStatus, ProArticlePricing, RemiseTier } from './types';

/**
 * En dessous d'un demi-millime, deux montants sont le même montant : c'est le
 * bruit d'arrondi de `NUMERIC(14,3)`, pas un changement de prix. Sert de seuil
 * à « ce document a-t-il vraiment bougé ? » — sans lui, on réécrirait des
 * documents et on notifierait des électriciens pour rien.
 */
export const PRO_PRICE_EPSILON = 0.0005;

/**
 * Tolérance de « ce prix a-t-il vraiment changé ? » sur une LIGNE : 10 millimes,
 * exactement celle de `pro_line_pricing_guard` (migration 20260702190000).
 *
 * Pourquoi pas un demi-millime : les lignes en base viennent de chemins qui
 * n'arrondissent pas dans le même ordre (le panier arrondit le HT ligne avant
 * d'appliquer la TVA, l'édition de devis dérive le HT depuis le TTC), et on
 * mesure jusqu'à 1 millime d'écart sur des lignes dont le prix n'a jamais
 * bougé — vérifié en prod sur DEV-2026-0006 (pttc 3,961 stocké contre 3,960
 * recalculé). Réagir à ce bruit réécrirait des devis intacts et enverrait des
 * notifications de « changement de prix » mensongères — l'erreur qui avait déjà
 * discrédité l'alerte de dérive catalogue côté ERP.
 *
 * S'aligner sur la tolérance du garde-fou rend les deux cohérents par
 * construction : en dessous, le trigger accepte encore la ligne telle quelle,
 * donc elle ne bloque rien.
 */
export const PRO_LINE_PRICE_TOLERANCE = 0.01;

function differs(a: number, b: number, tolerance = PRO_PRICE_EPSILON): boolean {
  return Math.abs(a - b) > tolerance;
}

/**
 * Un devis rejeté est mort : le réaligner n'aurait aucun effet utile et
 * enverrait une notification sur un document que l'électricien a abandonné.
 * En attente ET validé sont réalignés — un devis validé reste transformable en
 * commande (`Passer une commande` n'est pas conditionné au statut), donc il
 * doit rester au prix du jour.
 */
export function isDevisRepriceable(status: DevisStatus): boolean {
  return status !== 'rejetee';
}

/**
 * La commission suit le coût tant que la marchandise n'est pas partie.
 * `expediee` / `livree` / `retournee` / `rejetee` → figé pour toujours.
 */
export function isCommissionRecomputable(status: CommandeStatus): boolean {
  return status === 'en_attente' || status === 'confirmee' || status === 'emballee';
}

/** Ce que le domaine a besoin de savoir du catalogue pour retarifer. */
export interface ProCatalogSnapshot {
  articleId: string;
  salePriceHt: number;
  vatRate: number;
  /** Coût unitaire NET HT (remise fournisseur déduite) ; `null` = inconnu. */
  netCostHt: number | null;
  /** Surcouche de tarification Pro (prix fixes ou remises par palier). */
  pricing: ProArticlePricing | null;
}

/** Un article dont le prix a bougé sur un document donné. */
export interface ProPriceMove {
  articleId: string;
  oldUnitHt: number;
  newUnitHt: number;
  oldLineTtc: number;
  newLineTtc: number;
}

export interface DevisRepriceInput {
  lines: ReadonlyArray<ProDevisLineLike>;
  /**
   * Catalogue de TOUTES les lignes du devis, pas seulement des articles
   * modifiés : la commission d'en-tête est un total, la recalculer sans le coût
   * des autres lignes l'effondrerait.
   */
  catalog: ReadonlyMap<string, ProCatalogSnapshot>;
  /** Palier de prix du devis (`type_remise`) — on retarife au même palier. */
  tier: RemiseTier;
  commissionRatePct: number;
}

export interface DevisRepriceResult {
  lines: ProDevisLineLike[];
  pht: number;
  pttc: number;
  tva: number;
  totalCommission: number;
  /** Articles dont le prix a effectivement bougé. */
  moves: ProPriceMove[];
  /** Au moins une ligne ou un total a bougé → il faut écrire. */
  changed: boolean;
}

/**
 * Réaligne TOUTES les lignes d'un devis sur le catalogue courant, au palier du
 * devis.
 *
 * Pourquoi toutes et pas seulement l'article modifié : un devis dont une ligne
 * a dérivé (import, changement raté plus ancien) reste bloqué à la
 * transformation par le price-guard. Réaligner l'ensemble rend l'opération
 * auto-réparatrice, et une ligne déjà au bon prix ne produit aucun `move`.
 *
 * Une ligne dont l'article est absent du catalogue (supprimé, dépublié) est
 * conservée TELLE QUELLE : on ne sait pas la retarifer, et la mettre à zéro
 * détruirait le devis.
 */
export function repriceDevisLines(input: DevisRepriceInput): DevisRepriceResult {
  const moves: ProPriceMove[] = [];
  const lines: ProDevisLineLike[] = [];
  const benefitLines: SalesBenefitLineInput[] = [];
  let changed = false;

  for (const line of input.lines) {
    const snapshot = input.catalog.get(line.articleId);
    if (!snapshot) {
      lines.push({ ...line });
      continue;
    }

    const { unitHt, remisePct } = resolveTierLinePricing(
      { salePriceHt: snapshot.salePriceHt },
      snapshot.pricing,
      input.tier,
    );
    const totals = calculateProLineTotals({
      quantity: line.quantite,
      unitHt,
      remisePct,
      vatRate: snapshot.vatRate,
    });
    const repriced: ProDevisLineLike = {
      articleId: line.articleId,
      quantite: line.quantite,
      pht: totals.pht,
      pttc: totals.pttc,
      tva: snapshot.vatRate,
      remise: remisePct,
      montantRemise: totals.montantRemise,
    };
    lines.push(repriced);
    benefitLines.push({
      quantity: line.quantite,
      unitPriceHt: unitHt,
      discountPct: remisePct,
      unitCostHt: snapshot.netCostHt,
    });

    // On compare l'ARGENT (pht/pttc), jamais le champ `tva` : il est
    // sémantiquement ambigu en base — le checkout y écrit le TAUX, une vieille
    // voie d'édition y écrivait le MONTANT (constaté sur DEV-2026-0008 :
    // tva = 23,15 pour 121,850 HT). Le comparer ferait passer un devis intact
    // pour un devis dont le prix a changé. Un vrai changement de taux, lui, se
    // voit dans le TTC. La ligne réécrite porte le taux, forme canonique.
    if (
      differs(repriced.pht, line.pht, PRO_LINE_PRICE_TOLERANCE)
      || differs(repriced.pttc, line.pttc, PRO_LINE_PRICE_TOLERANCE)
    ) {
      changed = true;
      moves.push({
        articleId: line.articleId,
        oldUnitHt: grossUnitHt(line.pht, line.quantite, line.remise),
        newUnitHt: unitHt,
        oldLineTtc: line.pttc,
        newLineTtc: repriced.pttc,
      });
    }
  }

  const totals = calculateDevisTotals(lines);
  const totalCommission = calculateProCommission(benefitLines, input.commissionRatePct);

  return {
    lines,
    pht: totals.pht,
    pttc: totals.pttc,
    tva: totals.tva,
    totalCommission,
    moves,
    changed,
  };
}

/** Ligne de commande, vue du calcul de commission (prix DÉJÀ figé). */
export interface CommandeCommissionLine {
  /** `pro_commande_lines.id` — l'adapter n'écrit que le coût, ligne par ligne. */
  id: string;
  articleId: string;
  quantite: number;
  /** Total HT de la ligne, remise déduite (figé à la commande). */
  pht: number;
  /** Remise ligne en % (figée). */
  remise: number;
  /** Coût net HT figé sur la ligne ; `null` = inconnu. */
  unitCostHt: number | null;
}

/** Un article dont le coût d'achat a bougé sur une commande. */
export interface ProCostMove {
  articleId: string;
  oldCostHt: number | null;
  newCostHt: number;
}

export interface CommandeCommissionInput {
  lines: ReadonlyArray<CommandeCommissionLine>;
  /**
   * Nouveau coût net HT par article — seulement les articles dont le coût
   * catalogue vient de changer. Les autres lignes gardent leur coût figé.
   */
  newNetCostByArticle: ReadonlyMap<string, number | null>;
  commissionRatePct: number;
}

export interface CommandeCommissionResult {
  /** Coût à écrire sur chaque ligne (inchangé pour les lignes non touchées). */
  costs: Array<{ id: string; articleId: string; unitCostHt: number | null }>;
  totalCommission: number;
  moves: ProCostMove[];
  changed: boolean;
}

/**
 * Recalcule la commission d'une commande après un changement de COÛT d'achat.
 *
 * Le prix de vente ne bouge pas (la commande est passée) ; seul le coût est
 * remplacé, donc le bénéfice — et la commission qui en découle — suivent.
 *
 * Un coût devenu INCONNU (prix d'achat vidé du catalogue) ne remplace jamais un
 * coût connu : on garderait sinon la trace d'une saisie incomplète en effondrant
 * la commission de l'électricien à 0. Même prudence que partout ailleurs sur le
 * bénéfice — on ne devine pas un coût, mais on ne perd pas non plus celui qu'on
 * avait.
 */
export function recomputeCommandeCommission(
  input: CommandeCommissionInput,
): CommandeCommissionResult {
  const moves: ProCostMove[] = [];
  const costs: CommandeCommissionResult['costs'] = [];
  const benefitLines: SalesBenefitLineInput[] = [];
  let changed = false;

  for (const line of input.lines) {
    const hasUpdate = input.newNetCostByArticle.has(line.articleId);
    const incoming = hasUpdate ? input.newNetCostByArticle.get(line.articleId) ?? null : null;
    const usable = incoming != null && incoming > 0 ? round3(incoming) : null;
    const nextCost = usable ?? line.unitCostHt;

    costs.push({ id: line.id, articleId: line.articleId, unitCostHt: nextCost });
    benefitLines.push({
      quantity: line.quantite,
      unitPriceHt: grossUnitHt(line.pht, line.quantite, line.remise),
      discountPct: line.remise,
      unitCostHt: nextCost,
    });

    if (usable != null && (line.unitCostHt == null || differs(usable, line.unitCostHt))) {
      changed = true;
      moves.push({
        articleId: line.articleId,
        oldCostHt: line.unitCostHt,
        newCostHt: usable,
      });
    }
  }

  return {
    costs,
    totalCommission: calculateProCommission(benefitLines, input.commissionRatePct),
    moves,
    changed,
  };
}

/** Marge d'une ligne de commande au coût donné — utilisé par les tests/UI. */
export function commandeLineBenefit(line: CommandeCommissionLine): number {
  const { benefit } = calculateSalesLineBenefit({
    quantity: line.quantite,
    unitPriceHt: grossUnitHt(line.pht, line.quantite, line.remise),
    discountPct: line.remise,
    unitCostHt: line.unitCostHt,
  });
  return benefit;
}
